// The portal's calls to its own server code (functions/api).
import type { Approval, Client, Project } from "../lib/auth"
import { summary } from "../lib/studio"
import { locale } from "./i18n"

export type Entry = {
    project: Project
    totals: { net: number; vat: number; total: number }
    version: string
    approval: Approval | null
}
export type Loaded = { name: string; logo: string | null; lang?: "en" | "nl"; projects: Entry[] }

const post = (url: string, body: unknown) =>
    fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body), credentials: "same-origin" })

/** an old address: off to the client's new one, keeping the rest of the path */
export async function followMove(client: string) {
    const w = await fetch(`/api/hello?client=${encodeURIComponent(client)}`).then((r) => (r.ok ? r.json() : null)).catch(() => null)
    if (!w?.moved) return false
    const rest = location.pathname.split("/").slice(2).join("/")
    location.replace("/" + w.moved + (rest ? "/" + rest : ""))
    return true
}

/** null: not logged in (or not for this client) */
export async function loadProjects(client: string): Promise<Loaded | null> {
    const r = await fetch(`/api/projects?client=${encodeURIComponent(client)}`, { credentials: "same-origin" })
    if (r.status === 401) return null
    if (!r.ok) throw new Error(String(r.status))
    return r.json()
}

export async function login(client: string, password: string): Promise<"ok" | "wrong" | "many" | "error"> {
    try {
        const r = await post("/api/login", { client, password })
        if (r.ok) return "ok"
        if (r.status === 401) return "wrong"
        if (r.status === 429) return "many"
        return "error"
    } catch {
        return "error"
    }
}

export const logout = () => post("/api/logout", {})

// ---- the studio ----
export async function studioLogin(password: string): Promise<"ok" | "wrong" | "many" | "error"> {
    try {
        const r = await post("/api/studio/login", { password })
        if (r.ok) return "ok"
        if (r.status === 401) return "wrong"
        if (r.status === 429) return "many"
        return "error"
    } catch {
        return "error"
    }
}
export const studioLogout = () => post("/api/studio/logout", {})

export type Summary = {
    slug: string
    name: string
    logo: string | null
    lang: "en" | "nl"
    no: number
    projects: { id: string; title: string; reference: string; total: number; currency: string; approval: Approval | null; start: string; end: string; phases: { name: string; start: string; end: string }[] }[]
}
export type FullClient = { slug: string; name: string; logo: string | null; lang: "en" | "nl"; no: number; projects: Project[]; approvals: Record<string, Approval> }

// Cloudflare's store can still answer with what was there just before a change,
// for up to a minute. So the studio remembers its own recent changes and lays
// them over what comes back: a switch never flips back, a deleted client never
// reappears, a saved project never shows its old version.
const FRESH = 90_000
type Mine = { at: number; gone?: boolean; lang?: "en" | "nl"; record?: Pick<FullClient, "name" | "logo" | "projects" | "approvals"> & { no?: number }; goneProjects?: string[] }
const mine = new Map<string, Mine>()
const recent = (slug: string) => {
    const m = mine.get(slug)
    return m && Date.now() - m.at < FRESH ? m : null
}
function note(slug: string, change: Omit<Mine, "at">) {
    const m = recent(slug) || {}
    mine.set(slug, { ...m, ...change, at: Date.now() })
}
const last = new Map<string, FullClient>() // (the latest full client the studio has seen)

/** null: not logged in to the studio */
export async function studioClients(): Promise<Summary[] | null> {
    const r = await fetch("/api/studio/clients", { credentials: "same-origin" })
    if (r.status === 401) return null
    if (!r.ok) throw new Error(String(r.status))
    const list: Summary[] = (await r.json()).clients
    const out: Summary[] = []
    for (const c of list) {
        const m = recent(c.slug)
        if (m?.gone) continue
        out.push(m ? overSummary(c.slug, c, m) : c)
    }
    // (a client just made, not listed yet)
    for (const [slug] of mine) {
        const m = recent(slug)
        if (m?.record && !m.gone && !out.some((c) => c.slug === slug)) out.push(overSummary(slug, null, m))
    }
    return out
}
function overSummary(slug: string, c: Summary | null, m: Mine): Summary {
    const lang = m.lang || c?.lang || "en"
    if (m.record) return summary(slug, { ...m.record, no: m.record.no || c?.no, logo: m.record.logo || undefined, lang, pass: null as any } as Client) as Summary
    return { ...c!, lang, projects: c!.projects.filter((p) => !m.goneProjects?.includes(p.id)) }
}
export async function studioClient(slug: string): Promise<FullClient | null | "gone"> {
    const r = await fetch(`/api/studio/client?slug=${encodeURIComponent(slug)}`, { credentials: "same-origin" })
    if (r.status === 401) return null
    const m = recent(slug)
    if (m?.gone) return "gone"
    if (r.status === 404 && !m?.record) return "gone"
    if (!r.ok && r.status !== 404) throw new Error(String(r.status))
    let c: FullClient = r.ok ? await r.json() : { slug, lang: "en", ...m!.record! }
    if (m) {
        if (m.record) c = { ...c, ...m.record }
        if (m.lang) c.lang = m.lang
        if (m.goneProjects) c.projects = c.projects.filter((p) => !m.goneProjects!.includes(p.id))
    }
    last.set(slug, c)
    return c
}
/** { error } in words the studio can show, or what the server answered */
export async function studioSave(body: { slug: string; name: string; logo?: string | null; projects: Project[]; create?: boolean; rename?: string }) {
    try {
        const r = await post("/api/studio/client", body)
        const j = await r.json().catch(() => ({}))
        if (!r.ok) return { error: (j as any).error || (r.status === 401 ? "Logged out, log in again" : "Couldn't save, try again?") }
        const done = j as { ok: true; slug: string; password?: string; cleared?: string[]; no?: number }
        const before = body.create ? undefined : last.get(body.slug)
        const ids = new Set(body.projects.map((p) => p.id))
        const approvals = Object.fromEntries(Object.entries(before?.approvals || {}).filter(([id]) => ids.has(id) && !done.cleared?.includes(id)))
        if (body.create) mine.delete(body.slug)
        const at = done.slug || body.slug
        if (at !== body.slug) {
            note(body.slug, { gone: true })
            mine.delete(at)
            if (before) last.set(at, { ...before, slug: at })
        }
        note(at, { gone: false, goneProjects: undefined, record: { name: body.name.trim(), logo: body.logo || null, projects: body.projects, approvals, ...(done.no || before?.no ? { no: done.no || before!.no } : {}) } })
        if (body.create) note(at, { lang: "en" })
        else if (at !== body.slug && before) note(at, { lang: before.lang })
        return done
    } catch {
        return { error: "Couldn't save, try again?" }
    }
}
export async function studioNewPassword(slug: string): Promise<string | null> {
    const r = await post("/api/studio/password", { slug }).catch(() => null)
    return r && r.ok ? (await r.json()).password : null
}
export async function studioLang(slug: string, lang: "en" | "nl") {
    const r = await post("/api/studio/lang", { slug, lang }).catch(() => null)
    if (r?.ok) note(slug, { lang })
    return !!r && r.ok
}
export async function studioDelete(slug: string) {
    const r = await post("/api/studio/delete", { slug }).catch(() => null)
    if (r?.ok) note(slug, { gone: true })
    return !!r && r.ok
}

export async function approve(client: string, project: string, version: string): Promise<Approval | "changed" | "error"> {
    try {
        const r = await post("/api/approve", { client, project, version })
        if (r.status === 409) return "changed"
        if (!r.ok) return "error"
        return (await r.json()).approval
    } catch {
        return "error"
    }
}

const money = new Map<string, Intl.NumberFormat>()
export function fmtMoney(n: number, currency: string, cents = true) {
    const k = currency + cents + locale()
    if (!money.has(k))
        money.set(k, new Intl.NumberFormat(locale(), { style: "currency", currency, minimumFractionDigits: cents ? 2 : 0, maximumFractionDigits: cents ? 2 : 0 }))
    return money.get(k)!.format(n)
}
export const day = (iso: string) => new Date(iso + (iso.length === 10 ? "T12:00:00" : "")).getTime()
export const fmtDate = (iso: string, year = false) =>
    new Intl.DateTimeFormat(locale(), { day: "numeric", month: "short", ...(year ? { year: "numeric" } : {}) }).format(day(iso))
/** deletes what was picked: whole clients (no projects given) or some of their projects */
export async function studioRemove(items: { slug: string; projects?: string[] }[]) {
    const r = await post("/api/studio/remove", { items }).catch(() => null)
    if (!r?.ok) return false
    for (const { slug, projects } of items) {
        if (!projects) {
            note(slug, { gone: true })
            continue
        }
        const m = recent(slug)
        if (m?.record) note(slug, { record: { ...m.record, projects: m.record.projects.filter((p) => !projects.includes(p.id)) } })
        else note(slug, { goneProjects: [...(m?.goneProjects || []), ...projects] })
    }
    return true
}
